import clsx from 'clsx'
import { Slot, useGlobalSearchParams, usePathname, useRouter } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { CommonHeader } from '@/src/elements/templates/CommonLayout'
import { useAccessibilityContext } from '@/src/features/accessibility/context/AccessibilityContext'
import { AccessibilityHiddenTitle } from '@/src/features/accessibility/ui/components/AccessibilityHiddenTitle'
import { Protected } from '@/src/features/auth/ui/components/Protected'
import { ConversationPreviewList } from '@/src/features/message/ui/components/ConversationPreviewList'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { usePageType } from '@/src/general/hooks/usePageType'
import { useRoutes } from '@/src/general/utils/routes'

import { useConversationsPreviewQuery } from '../../services/useConversationsPreviewQuery'
import { useConversationsListStore } from '../../stores/useConversationsListStore'
import { EmptyMessagesScreen } from '../screens/MessagesScreen/components/EmptyMessageScreen'

export const DesktopMessagesScreenLayout = (): ReactNode => {
  const { initializeRef } = useAccessibilityContext()

  const searchParams = useGlobalSearchParams()
  const userId = Number(searchParams.userId) || undefined
  const { routes } = useRoutes()
  const { navigate } = useRouter()
  const { sm, xm } = useDimensions()
  const handleBackPress = () => navigate(routes.messages())
  const pathname = usePathname()
  const { background } = usePageType()
  const isInConversation = /messages\/[0-9]+/.test(pathname)

  const unread = useConversationsListStore((state) => state.unread)
  const { data: conversations } = useConversationsPreviewQuery()
  const isEmpty = !unread && conversations && conversations.length === 0
  const isCreatingConversation = userId !== undefined

  const commonHeaderProps =
    (sm || xm) && isInConversation
      ? {
          leftButtons: [
            {
              startIcon: ICON.ARROW_LEFT,
              onPress: handleBackPress,
              testID: TEST_ID.CONVERSATION.HEADER.BACK,
              type: 'icon' as const,
            },
          ],
        }
      : {}

  return (
    <Protected searchParams={searchParams}>
      <View className={clsx('h-full bg-white', !isEmpty && 'divide-y divide-layout-divider-contrast-high')}>
        <View className="relative">
          <CommonHeader {...commonHeaderProps} />
        </View>
        <View className="flex w-full flex-1 flex-row divide-x divide-layout-divider-contrast-high">
          <AccessibilityHiddenTitle
            ref={(ref) => {
              initializeRef(ref, 'main')
            }}
          >
            <FormattedMessage id="app.accessibility.messages" />
          </AccessibilityHiddenTitle>
          {isEmpty && !isCreatingConversation ? (
            <EmptyMessagesScreen />
          ) : (
            <>
              <View className="flex h-full w-[36%] max-w-[300px] flex-col divide-y  divide-layout-divider-contrast-high">
                <ConversationPreviewList
                  conversationId={Number(searchParams.conversationId) || undefined}
                  openedConversationId={Number(searchParams.openedConversationId) || undefined}
                />
              </View>
              <View
                className={clsx(
                  'flex h-full flex-1 flex-col divide-y divide-layout-divider-contrast-high',
                  background === 'primary-light' && 'bg-primary-bg-light',
                )}
              >
                <Slot />
              </View>
            </>
          )}
        </View>
      </View>
    </Protected>
  )
}
